<script>
import axios from 'axios';
import moment from 'moment';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { AgGridVue } from 'ag-grid-vue3';
import { themeAlpine } from 'ag-grid-community';

export default {
  name: 'CallsGridView',
  components: {
    AgGridVue,
    VueDatePicker
  },
  data() {
    return {
      gridTheme: themeAlpine,
      gridApi: null,
      gridReady: false,
      columnDefs: [
        {
          headerName: 'ID',
          field: 'id',
          width: 80,
          sortable: true,
          filter: true,
          cellRenderer: (params) => {
            return `<a href="/call/${params.value}" class="text-decoration-none">${params.value}</a>`;
          }
        },
        {
          headerName: 'Date/Time',
          field: 'created_at',
          width: 180,
          sortable: true,
          filter: 'agDateColumnFilter',
          valueFormatter: (params) => {
            return params.value ? moment(params.value).format('YYYY-MM-DD HH:mm') : '';
          }
        },
        {
          headerName: 'Street',
          field: 'street',
          width: 250,
          sortable: true,
          filter: true
        },
        {
          headerName: 'City',
          field: 'city',
          width: 150,
          sortable: true,
          filter: true
        },
        {
          headerName: 'Call Type',
          field: 'call_type',
          width: 200,
          sortable: true,
          filter: true
        },
        {
          headerName: 'Final Call Type',
          field: 'final_call_type',
          width: 200,
          sortable: true,
          filter: true
        },
        {
          headerName: 'Department',
          field: 'department',
          width: 180,
          sortable: true,
          filter: true
        },
        {
          headerName: 'Mutual Aid',
          field: 'mutual_aid',
          width: 150,
          sortable: true,
          filter: true
        },
        {
          headerName: 'Radio',
          field: 'radio_readability',
          width: 100,
          sortable: true,
          filter: true,
          valueFormatter: (params) => {
            return params.value !== null ? `${params.value}/5` : '';
          }
        }
      ],
      defaultColDef: {
        resizable: true,
        sortable: true,
        filter: true
      },
      rowData: [],
      totalRows: 0,
      isLoading: false,
      error: null,
      dateRange: [
        moment().subtract(30, 'days').toDate(),
        moment().toDate()
      ],
      paginationPageSize: 100,
      currentPage: 0,
      periodPresets: [
        { label: 'Today', days: 0 },
        { label: 'Last 7 Days', days: 7 },
        { label: 'Last 30 Days', days: 30 },
        { label: 'Last 90 Days', days: 90 },
        { label: 'This Year', days: 365 }
      ]
    };
  },
  watch: {
    dateRange: {
      handler() {
        // Only load calls if grid is ready
        if (!this.gridReady) {
          return;
        }
        this.currentPage = 0;
        this.loadCalls();
      },
      deep: true
    }
  },
  mounted() {
    // Don't load calls here - wait for grid to be ready
  },
  methods: {
    async loadCalls() {
      this.isLoading = true;
      this.error = null;

      try {
        const params = {
          page: this.currentPage,
          page_size: this.paginationPageSize
        };

        if (this.dateRange && this.dateRange.length === 2) {
          params.start_date = moment(this.dateRange[0]).startOf('day').toISOString();
          params.end_date = moment(this.dateRange[1]).endOf('day').toISOString();
        }

        const response = await axios.get('/calls/grid', { params });
        this.rowData = response.data.rows;
        this.totalRows = response.data.total;
      } catch (err) {
        console.error('Error loading calls:', err);
        this.error = 'Failed to load calls. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridReady = true;
      // Load initial data once grid is ready
      this.loadCalls();
    },

    onPaginationChanged() {
      // Defensive check to prevent errors
      if (!this.gridApi) {
        return;
      }

      const newPage = this.gridApi.paginationGetCurrentPage();
      if (newPage !== this.currentPage) {
        this.currentPage = newPage;
        this.loadCalls();
      }
    },

    setPeriod(days) {
      // Prevent watcher from firing until grid is ready
      if (!this.gridReady) {
        return;
      }

      if (days === 0) {
        // Today
        this.dateRange = [
          moment().startOf('day').toDate(),
          moment().endOf('day').toDate()
        ];
      } else {
        this.dateRange = [
          moment().subtract(days, 'days').startOf('day').toDate(),
          moment().endOf('day').toDate()
        ];
      }
    },

    exportToCSV() {
      if (this.gridApi) {
        this.gridApi.exportDataAsCsv({
          fileName: `calls-${moment().format('YYYY-MM-DD')}.csv`
        });
      }
    }
  }
};
</script>

<template>
  <div class="container-fluid mt-4">
    <div class="card">
      <div class="card-header">
        <div class="d-flex justify-content-between align-items-center">
          <h4 class="mb-0">
            <i class="bi bi-table me-2"></i>Calls Grid
          </h4>
          <button
            @click="exportToCSV"
            class="btn btn-success btn-sm"
            :disabled="isLoading || rowData.length === 0">
            <i class="bi bi-download me-1"></i>Export CSV
          </button>
        </div>
      </div>

      <div class="card-body">
        <!-- Filters -->
        <div class="row mb-3">
          <div class="col-md-6">
            <label class="form-label fw-bold">
              <i class="bi bi-calendar-range me-1"></i>Date Range:
            </label>
            <VueDatePicker
              v-model="dateRange"
              range
              :enable-time-picker="false"
              format="yyyy-MM-dd"
              :clearable="false"
            />
          </div>
          <div class="col-md-6">
            <label class="form-label fw-bold">Quick Periods:</label>
            <div class="btn-group w-100" role="group">
              <button
                v-for="preset in periodPresets"
                :key="preset.label"
                @click="setPeriod(preset.days)"
                class="btn btn-outline-primary btn-sm">
                {{ preset.label }}
              </button>
            </div>
          </div>
        </div>

        <!-- Stats -->
        <div class="alert alert-info d-flex justify-content-between align-items-center mb-3">
          <span>
            <i class="bi bi-info-circle me-2"></i>
            <strong>{{ totalRows }}</strong> calls found
          </span>
          <span v-if="isLoading">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading...
          </span>
        </div>

        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          {{ error }}
        </div>

        <!-- AG Grid -->
          <AgGridVue
            :columnDefs="columnDefs"
            :rowData="rowData"
            :defaultColDef="defaultColDef"
            :pagination="true"
            :paginationPageSize="paginationPageSize"
            :suppressPaginationPanel="false"
            dom-layout="autoHeight"
            :theme="gridTheme"
            @grid-ready="onGridReady"
            @pagination-changed="onPaginationChanged"
          >
          </AgGridVue>
      </div>
    </div>
  </div>
</template>

<style scoped>
.btn-group {
  flex-wrap: wrap;
}

.btn-group .btn {
  flex: 1;
  min-width: fit-content;
}

@media (max-width: 768px) {
  .ag-theme-alpine {
    height: 500px !important;
  }
}
</style>